// src/components/ecommerce/EcommerceManager.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { 
  X,
  Store,
  Package,
  ShoppingCart,
  Users,
  BarChart3,
  Settings,
  Plus,
  Grid,
  List,
  Search,
  Filter,
  Download,
  Upload,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  Star,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  DollarSign,
  Truck,
  Tag,
  Heart,
  MessageCircle,
  Globe,
  Palette
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ecommerceService } from '../../services/ecommerceService';
import { ProductManager } from './ProductManager';
import { OrderManager } from './OrderManager';
import { CategoryManager } from './CategoryManager';
import { CustomerManager } from './CustomerManager';
import { InventoryManager } from './InventoryManager';
import { AnalyticsManager } from './AnalyticsManager';
import { HeroManager } from './HeroManager';
import { FeaturedManager } from './FeaturedManager';
import { PromotionsManager } from './PromotionsManager';
import { EcommerceSettings } from './EcommerceSettings';
import { ServicesManager } from './ServicesManager';

import { getStoreTypeConfig } from './utils/storeTypeConfig';

export const EcommerceManager = ({ bookData, bookId, onUpdate, onClose }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [storeData, setStoreData] = useState({});
  const [dashboardStats, setDashboardStats] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);

  const storeType = storeData?.storeType || 'general';
  const storeConfig = useMemo(() => getStoreTypeConfig(storeType), [storeType]);
  const isServiceStore = storeType === 'services';

  useEffect(() => {
    loadDashboardData();
  }, [bookId]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load store data with defaults
      const store = await ecommerceService.getStore(bookId);
      setStoreData(store || {});

      // Load dashboard statistics with defaults
      const stats = await ecommerceService.getStoreAnalytics(bookId, 30);
      setDashboardStats(stats || {
        pendingOrders: 0,
        lowStockCount: 0,
        totalRevenue: 0,
        averageOrderValue: 0,
        conversionRate: 0
      });

      // Load recent activity with defaults
      const activity = await ecommerceService.getRecentActivity(bookId);
      setRecentActivity(activity || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Set safe defaults on error
      setStoreData({});
      setDashboardStats({
        pendingOrders: 0,
        lowStockCount: 0,
        totalRevenue: 0,
        averageOrderValue: 0,
        conversionRate: 0
      });
      setRecentActivity([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStoreUpdate = async (updates) => {
    try {
      await ecommerceService.updateStore(bookId, updates);
      setStoreData(prev => ({ ...prev, ...updates }));
      onUpdate?.(updates);
    } catch (error) {
      console.error('Error updating store:', error);
    }
  };

  const tabs = [
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      icon: BarChart3, 
      description: 'Overview and analytics' 
    },
    {
      id: 'products',
      label: storeConfig.itemNamePlural, 
      icon: storeConfig.icon, 
      description: `Manage your ${storeConfig.itemNamePlural.toLowerCase()}`
    },
    { 
      id: 'hero', 
      label: 'Hero Section', 
      icon: Star 
    }, 
    { 
      id: 'featured', 
      label: 'Featured', 
      icon: TrendingUp 
    }, 
    { 
      id: 'promotions', 
      label: 'Promotions', 
      icon: Tag 
    }, 
    { 
      id: 'orders', 
      label: 'Orders', 
      icon: ShoppingCart, 
      description: 'View and manage orders',
      badge: dashboardStats.pendingOrders 
    },
    { 
      id: 'customers', 
      label: 'Customers', 
      icon: Users, 
      description: 'Customer management',
      badge: storeData.customerCount 
    },
    { 
      id: 'categories', 
      label: 'Categories', 
      icon: Grid, 
      description: 'Organize your products' 
    },
    { 
      id: 'inventory', 
      label: 'Inventory', 
      icon: Package, 
      description: 'Stock management',
      alert: dashboardStats.lowStockCount > 0 
    },
    { 
      id: 'analytics', 
      label: 'Analytics', 
      icon: TrendingUp, 
      description: 'Performance insights' 
    },
    { 
      id: 'settings', 
      label: 'Settings', 
      icon: Settings, 
      description: 'Store configuration' 
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading store management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-sm border border-gray-200 overflow-hidden flex flex-col h-screen pb-8">
      {/* Header */}
      <div
          className="text-white p-4"
          style={{
            background: `linear-gradient(to right, ${bookData?.design?.primaryColor || '#2563eb'}, ${bookData?.design?.accentColor || '#9333ea'})`,
          }}
        >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20">
              
              <Store className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                {bookData.title || 'eCommerce Store'}
              </h1>
              <p className="text-blue-100">
                {bookData.tagline || 'Manage your online store'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Quick Stats */}
            <div className="text-center">
              <div className="text-2xl font-bold">{dashboardStats.totalRevenue || 0}</div>
              <div className="text-xs text-blue-100">Revenue (30d)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{dashboardStats.totalOrders || 0}</div>
              <div className="text-xs text-blue-100">Orders (30d)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{storeData.productCount || 0}</div>
              <div className="text-xs text-blue-100">Products</div>
            </div>

            {/* Preview Button */}
            <a
              href={`/books/${bookId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            >
              <Eye className="h-4 w-4" />
              Preview Store
            </a>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="flex overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-3 px-6 py-4 border-b-2 whitespace-nowrap transition-colors relative ${
                  activeTab === tab.id
                    ? 'border-blue-500 bg-white text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Icon className="h-5 w-5" />
                <div className="text-left">
                  <div className="font-medium">{tab.label}</div>
                  <div className="text-xs opacity-75">{tab.description}</div>
                </div>
                
                {/* Badges and Alerts */}
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                    {tab.badge}
                  </span>
                )}
                {tab.alert && (
                  <AlertCircle className="h-4 w-4 text-red-500" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6 flex-1 overflow-y-auto">
        {activeTab === 'dashboard' && (
          <DashboardContent 
            bookData={bookData}
            stats={dashboardStats}
            recentActivity={recentActivity}
            onRefresh={loadDashboardData}
          />
        )}

        {activeTab === 'hero' && (
          <HeroManager 
            storeId={bookId}
            bookData={bookData}
            onUpdate={handleStoreUpdate}
          />
        )}

        {activeTab === 'products' && (
          isServiceStore ? (
            <ServicesManager 
              storeId={bookId}
              storeData={storeData}
              onUpdate={handleStoreUpdate}
            />
          ) : (
            <ProductManager 
              storeId={bookId}
              storeData={storeData}
              onUpdate={handleStoreUpdate}
            />
          )
        )}

        {activeTab === 'featured' && (
          <FeaturedManager 
            storeId={bookId}
            bookData={bookData}
            onUpdate={handleStoreUpdate}
          />
        )}

        {activeTab === 'promotions' && (
          <PromotionsManager 
            storeId={bookId}
            bookData={bookData}
            onUpdate={handleStoreUpdate}
          />
        )}

        {activeTab === 'orders' && (
          <OrderManager 
            storeId={bookId}
            bookData={bookData}
          />
        )}

        {activeTab === 'customers' && (
          <CustomerManager 
            storeId={bookId}
            bookData={bookData}
          />
        )}

        {activeTab === 'categories' && (
          <CategoryManager 
            storeId={bookId}
            bookData={bookData}
            onUpdate={handleStoreUpdate}
          />
        )}

        {activeTab === 'inventory' && (
          <InventoryManager 
            storeId={bookId}
            bookData={bookData}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsManager 
            storeId={bookId}
            bookData={bookData}
          />
        )}

        {activeTab === 'settings' && (
          <EcommerceSettings 
            book={bookData}
            isOpen={true}
            onClose={() => {}}
            onUpdate={handleStoreUpdate}
          />
        )}
      </div>
    </div>
  );
};

// Dashboard Content Component
const DashboardContent = ({ bookData, stats, recentActivity, onRefresh, storeConfig }) => {
  return (
    <div className="space-y-6">

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                R{(stats.totalRevenue || 0).toLocaleString()}
              </p>
              <p className="text-sm text-green-600">+12% from last month</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalOrders || 0}
              </p>
              <p className="text-sm text-blue-600">+5% from last month</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <ShoppingCart className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Products</p>
              <p className="text-2xl font-bold text-gray-900">
                {bookData.productCount || 0}
              </p>
              <p className="text-sm text-purple-600">
                {stats.lowStockCount || 0} low stock
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Package className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Customers</p>
              <p className="text-2xl font-bold text-gray-900">
                {bookData.customerCount || 0}
              </p>
              <p className="text-sm text-orange-600">+8% from last month</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Users className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
              <button 
                onClick={onRefresh}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="p-6">
            {recentActivity.length > 0 ? (
              <div className="space-y-4">
                {recentActivity.slice(0, 5).map((activity, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      {activity.type === 'order' && <ShoppingCart className="h-4 w-4 text-blue-600" />}
                      {activity.type === 'product' && <Package className="h-4 w-4 text-green-600" />}
                      {activity.type === 'customer' && <Users className="h-4 w-4 text-purple-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {activity.description}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(activity.timestamp?.toDate?.() || activity.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No recent activity</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Quick Stats</h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Conversion Rate</span>
              <span className="text-sm font-medium">3.2%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Average Order Value</span>
              <span className="text-sm font-medium">R{(stats.averageOrderValue || 0).toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Top Category</span>
              <span className="text-sm font-medium">{stats.topCategory || 'Electronics'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Store Views (30d)</span>
              <span className="text-sm font-medium">{stats.storeViews || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Cart Abandonment</span>
              <span className="text-sm font-medium text-red-600">23%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts and Notifications */}
      {((stats?.lowStockCount || 0) > 0 || (stats?.pendingOrders || 0) > 0) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-yellow-800">Attention Required</h3>
              <div className="mt-2 space-y-1 text-sm text-yellow-700">
                {(stats?.lowStockCount || 0) > 0 && (
                  <p>• {stats.lowStockCount} products are running low on stock</p>
                )}
                {(stats?.pendingOrders || 0) > 0 && (
                  <p>• {stats.pendingOrders} orders are pending processing</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};